import _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { INPUT_STATES } from '../components/forms/forms.utils';
import { usePrevious } from './usePrevious';

const REQUIRED_RESOURCE_FIELDS = ['getValue', 'setValue', 'isFetching', 'isSubmitting'];

const validateResource = (config) => {
  REQUIRED_RESOURCE_FIELDS.forEach((field) => {
    if (_.get(config, field) === undefined) {
      throw new Error(`'useResourceInput' - ${field} is required on a resource`);
    }
  });
};

export const useResourceInput = (field, resource, defaultValue = '', options = {}) => {
  validateResource(resource);

  const prevIsFetching = usePrevious(resource.isFetching);
  const [state, setState] = useState(INPUT_STATES.READ);
  const [touched, setTouched] = useState(false);
  const [draft, setDraft] = useState(
    resource.getValue(field) === false ? false : resource.getValue(field) || defaultValue
  );
  const [error, setError] = useState('');
  const [validationError, setValidationError] = useState('');
  const [isEditingSelect, setIsEditingSelect] = useState(false);

  // TODO perhaps make different hook for Datepicker input
  // value, callback specifically to handle Datepicker calendar click
  const handleSubmit = (value, callback) => {
    const newValue = value !== null && value !== undefined ? value : draft;
    if (resource.validators?.[field]) {
      const error = resource.validators?.[field](newValue);
      setValidationError(error);
      if (error) {
        return;
      }
    }

    setError('');
    setState(INPUT_STATES.SUBMITTING);
    if (!options.isSwitch && value !== null && value !== undefined) {
      setDraft(value);
    }
    resource.setValue(newValue, field, {
      onSuccess: () => {
        setTouched(false);
        setState(INPUT_STATES.READ);
        callback && callback(true);
      },
      onError: (message) => {
        setError(message);
        setState(INPUT_STATES.EDIT);
        callback && callback();
      }
    });
  };

  const handleCancel = () => {
    resource?.onCancel && resource.onCancel();
    setError('');
    setValidationError('');
    setState(INPUT_STATES.READ);
    setDraft(resource.getValue(field));
  };

  const handleEditStart = useCallback(() => {
    setState(INPUT_STATES.EDIT);
  }, []);

  const handleChange = useCallback(
    (value) => {
      setError('');

      if (!touched) {
        setTouched(true);
      }
      setDraft(value);
    },
    [touched]
  );

  const handleSelectOpen = () => {
    setIsEditingSelect(true);
  };

  const handleSelectClose = (value) => {
    if (isEditingSelect) {
      setValidationError('');
      const hasChanged = !_.isEqual(resource?.getValue(field), value);
      if (hasChanged) {
        handleSubmit(value);
      }
    }
    setIsEditingSelect(false);
  };

  // populate on fetched
  useEffect(() => {
    if (prevIsFetching && !resource.isFetching) {
      setDraft(resource.getValue(field));
    }
  }, [resource.isFetching, resource, field, prevIsFetching]);

  return {
    value: draft,
    hasError: !!error,
    description: error ?? '',
    validationError,
    disabled: resource.isFetching || resource.isSubmitting,
    state,
    onChange: handleChange,
    onSubmit: !touched && !options?.ignoreTouched ? undefined : handleSubmit,
    onCancel: handleCancel,
    onEditStart: handleEditStart,
    onOpen: handleSelectOpen,
    onClose: handleSelectClose
  };
};
